<script setup lang="ts">
import { computed, ref, watch } from "vue";

import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { reload } from "@/error/reload";
import { useBoardStore } from "@/store/board";
import { useConnectionStore } from "@/store/connection";

const boardError = computed(() => useBoardStore().board?.state.state === "error");
const ok = computed(() => useConnectionStore().isConnected && !boardError.value);
const showMessage = ref(false);

watch(
  ok,
  () => {
    // wait a little to avoid the disconnect message when reloading the page (firefox)
    setTimeout(
      () => {
        showMessage.value = !ok.value;
      },
      ok.value ? 0 : 100,
    );
  },
  { immediate: true },
);

function reloadPage() {
  reload("manual-connection-status");
}
</script>

<template>
  <div id="connection-status" role="alert" data-no-screenshot>
    <div v-if="showMessage" class="no-net">
      <SvgIcon name="general/disconnected" class="disconnected" width="16" height="16" />
      <div class="message">
        {{ $t("connectionStatus.notWorking") }}
        <span v-if="!boardError">
          {{ $t("connectionStatus.autoReconnect") }}
        </span>
      </div>
      <div>
        <BaseButton variant="ghost" @click="reloadPage">
          {{ $t("connectionStatus.refreshPage") }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/z-index";

#connection-status {
  z-index: z-index.$menu;
  position: fixed;
  top: variables.$top-header-height;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 12px;
  line-height: 18px;

  .disconnected {
    color: colors.$destructive;
  }

  .no-net {
    background-color: colors.$alternative-red-background-light;
    border: 1px solid colors.$destructive;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    padding: 10px 12px;
    border-radius: variables.$common-border-radius;

    .message {
      font-size: 14px;
      font-weight: font.$weight-semi-bold;
      margin-left: 10px;
    }
  }
}
</style>
